import React, { useCallback, useEffect, useRef, useState } from 'react';
import { Outlet, useLocation, useNavigate } from 'react-router-dom';
import { IconX } from '@tabler/icons-react';
import LegalConsentGuard from '../components/LegalConsentGuard';
import BottomTab from '../components/BottomTab';
import MainAppHeader from '../components/MainAppHeader';
import MyPage from '../pages/MyPage';
import ServiceMenuPage from '../pages/ServiceMenuPage';
import { canEnterMainApp } from '../lib/onboardingStorage';
import { useDailyLoginPoints } from '../hooks/useDailyLoginPoints';
import { useSwipeToCloseDrawer } from '../hooks/useSwipeToCloseDrawer';

function TabLayout() {
  const PULL_THRESHOLD = 90;
  const MAX_PULL = 120;
  const PULL_START_DEADZONE = 14;
  const HORIZONTAL_CANCEL_THRESHOLD = 18;
  useDailyLoginPoints();
  const [serviceMenuOpen, setServiceMenuOpen] = useState(false);
  const [serviceMenuClosing, setServiceMenuClosing] = useState(false);
  const [serviceMenuFocus, setServiceMenuFocus] = useState(null);
  const [pullDistance, setPullDistance] = useState(0);
  const [refreshing, setRefreshing] = useState(false);
  const [profileDrawerOpen, setProfileDrawerOpen] = useState(false);
  const profileDrawerSwipe = useSwipeToCloseDrawer(profileDrawerOpen, () => setProfileDrawerOpen(false));
  const navigate = useNavigate();
  const location = useLocation();
  const isShortsImmersive = location.pathname === '/shorts' || location.pathname === '/shorts/create';
  const isPullRefreshEnabled = location.pathname === '/home';
  const isHomeTab = location.pathname === '/home';
  const mainRef = useRef(null);
  const pullStartYRef = useRef(0);
  const pullStartXRef = useRef(0);
  const pullDistanceRef = useRef(0);
  const pullTrackingRef = useRef(false);
  const homePullRefreshRef = useRef(null);
  const homeProfileDrawerOpenRef = useRef(false);

  const registerHomePullRefresh = useCallback((fn) => {
    homePullRefreshRef.current = fn;
  }, []);

  const registerHomeProfileDrawerOpen = useCallback((isOpen) => {
    homeProfileDrawerOpenRef.current = !!isOpen;
  }, []);

  const isProfileDrawerBlockingTouch = () =>
    profileDrawerOpen || homeProfileDrawerOpenRef.current;

  const isAtTop = () => {
    if (isHomeTab) {
      const homeScroll = document.querySelector('.mingo-home__scroll');
      if (homeScroll && homeScroll.scrollTop > 0) return false;
    }
    const mainTop = mainRef.current?.scrollTop ?? 0;
    const docTop = window.scrollY || window.pageYOffset || document.scrollingElement?.scrollTop || 0;
    return mainTop <= 0 && docTop <= 0;
  };

  const handleTouchStart = (e) => {
    if (!isPullRefreshEnabled) return;
    if (isProfileDrawerBlockingTouch()) return;
    if (refreshing || serviceMenuOpen) return;
    if (e.touches.length !== 1) return;
    if (!isAtTop()) return;

    pullStartYRef.current = e.touches[0].clientY;
    pullStartXRef.current = e.touches[0].clientX;
    pullTrackingRef.current = true;
    pullDistanceRef.current = 0;
    setPullDistance(0);
  };

  const handleTouchMove = (e) => {
    if (!isPullRefreshEnabled) return;
    if (isProfileDrawerBlockingTouch()) return;
    if (!pullTrackingRef.current || refreshing) return;

    const deltaY = e.touches[0].clientY - pullStartYRef.current;
    const deltaX = Math.abs(e.touches[0].clientX - pullStartXRef.current);
    if (deltaX > HORIZONTAL_CANCEL_THRESHOLD) {
      pullTrackingRef.current = false;
      pullDistanceRef.current = 0;
      setPullDistance(0);
      return;
    }
    if (deltaY < PULL_START_DEADZONE) return;

    const delta = deltaY - PULL_START_DEADZONE;
    if (delta <= 0) {
      pullDistanceRef.current = 0;
      setPullDistance(0);
      return;
    }
    if (!isAtTop()) {
      pullTrackingRef.current = false;
      pullDistanceRef.current = 0;
      setPullDistance(0);
      return;
    }

    const next = Math.min(MAX_PULL, delta * 0.6);
    pullDistanceRef.current = next;
    setPullDistance(next);
    e.preventDefault();
  };

  const handleTouchEnd = () => {
    if (!isPullRefreshEnabled) return;
    if (isProfileDrawerBlockingTouch()) return;
    if (!pullTrackingRef.current) return;
    pullTrackingRef.current = false;

    const shouldRefresh = pullDistanceRef.current >= PULL_THRESHOLD;
    pullDistanceRef.current = 0;
    setPullDistance(0);

    if (!shouldRefresh) return;

    setRefreshing(true);
    const refreshFn = homePullRefreshRef.current;
    Promise.resolve(typeof refreshFn === 'function' ? refreshFn() : null)
      .catch(() => {})
      .finally(() => {
        setRefreshing(false);
      });
  };

  useEffect(() => {
    if (!canEnterMainApp()) {
      navigate('/splash', { replace: true });
    }
  }, [navigate, location.pathname]);

  useEffect(() => {
    setProfileDrawerOpen(false);
  }, [location.pathname]);

  useEffect(() => {
    if (serviceMenuOpen) setProfileDrawerOpen(false);
  }, [serviceMenuOpen]);

  const openServiceMenu = useCallback((opts) => {
    setServiceMenuClosing(false);
    const sec = opts?.section === 'trade' || opts?.section === 'life' ? opts.section : null;
    setServiceMenuFocus(sec);
    setServiceMenuOpen(true);
  }, []);

  const closeServiceMenu = useCallback(() => {
    if (!serviceMenuOpen || serviceMenuClosing) return;
    setServiceMenuClosing(true);
    window.setTimeout(() => {
      setServiceMenuOpen(false);
      setServiceMenuClosing(false);
      setServiceMenuFocus(null);
    }, 300);
  }, [serviceMenuOpen, serviceMenuClosing]);

  return (
    <div
      className="mingo-app-root"
      onTouchStart={handleTouchStart}
      onTouchMove={handleTouchMove}
      onTouchEnd={handleTouchEnd}
      onTouchCancel={handleTouchEnd}
      style={{ touchAction: 'pan-y' }}
    >
      {(isPullRefreshEnabled && (pullDistance > 0 || refreshing)) ? (
        <div
          style={{
            position: 'fixed',
            top: 8,
            left: '50%',
            transform: 'translateX(-50%)',
            zIndex: 200,
            padding: '8px 12px',
            borderRadius: 999,
            background: 'rgba(255,255,255,0.96)',
            border: '1px solid #FFD9C7',
            color: '#FF6B2C',
            fontSize: 13,
            fontWeight: 700,
            boxShadow: '0 2px 10px rgba(0,0,0,0.08)',
          }}
          aria-live="polite"
        >
          {refreshing ? '새로고침 중...' : (pullDistance >= PULL_THRESHOLD ? '놓으면 새로고침' : '↓ 새로고침')}
        </div>
      ) : null}
      <div
        className={`mingo-app-shell${serviceMenuOpen ? ' mingo-app-shell--no-tab' : ''}`}
      >
        {serviceMenuOpen ? (
          <ServiceMenuPage onBack={closeServiceMenu} isClosing={serviceMenuClosing} focusSection={serviceMenuFocus} />
        ) : (
          <LegalConsentGuard>
          <>
            <main
              ref={mainRef}
              className={`mingo-app-main mingo-app-main--stack-scroll${isShortsImmersive ? '' : ' mingo-app-main--with-main-header'}${isHomeTab ? ' mingo-app-main--home-embedded-header' : ''}`}
              style={{ overflowX: 'hidden', width: '100%' }}
            >
              {!isShortsImmersive && !isHomeTab ? (
                <MainAppHeader
                  onOpenServiceMenu={openServiceMenu}
                  onOpenDrawer={() => setProfileDrawerOpen(true)}
                />
              ) : null}
              <div className="mingo-app-main__outlet">
                <Outlet
                  context={{
                    onOpenServiceMenu: openServiceMenu,
                    onOpenProfileDrawer: () => setProfileDrawerOpen(true),
                    registerHomePullRefresh,
                    registerHomeProfileDrawerOpen,
                  }}
                />
              </div>
            </main>
            {!isHomeTab && profileDrawerOpen ? (
              <div
                className="mingo-profile-drawer__backdrop"
                onClick={() => setProfileDrawerOpen(false)}
                onTouchStart={profileDrawerSwipe.onTouchStart}
                onTouchEnd={profileDrawerSwipe.onTouchEnd}
              >
                <button
                  type="button"
                  className="mingo-profile-drawer__backdrop-close"
                  onClick={(e) => {
                    e.stopPropagation();
                    setProfileDrawerOpen(false);
                  }}
                >
                  <IconX size={18} stroke={2} aria-hidden />
                </button>
              </div>
            ) : null}
            {!isHomeTab ? (
              <div
                className={`mingo-profile-drawer ${profileDrawerOpen ? 'mingo-profile-drawer--open' : ''}`}
                onTouchStart={(e) => {
                  e.stopPropagation();
                  profileDrawerSwipe.onTouchStart(e);
                }}
                onTouchMove={(e) => e.stopPropagation()}
                onTouchEnd={(e) => {
                  e.stopPropagation();
                  profileDrawerSwipe.onTouchEnd(e);
                }}
              >
                <div
                  className="mingo-profile-drawer__page-wrap"
                  onTouchStart={(e) => e.stopPropagation()}
                  onTouchMove={(e) => e.stopPropagation()}
                  onTouchEnd={(e) => e.stopPropagation()}
                >
                  <MyPage isDrawerMode />
                </div>
              </div>
            ) : null}
            {!isShortsImmersive ? <BottomTab /> : null}
          </>
          </LegalConsentGuard>
        )}
      </div>
    </div>
  );
}

export default TabLayout;
